import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { getCurrentPosition, getCountryCode } from "../services/GeoService";

const GeoContext = createContext(null);

export const GeoProvider = ({ children }) => {
  const [geoCountryCode, setGeoCountryCode] = useState(null);
  const [geoReady, setGeoReady] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);

  const hasResolvedRef = useRef(false);
  const pendingPromiseRef = useRef(null);

  const resetGeoState = useCallback(() => {
    setGeoCountryCode(null);
    setGeoReady(false);
    setGeoLoading(false);
    hasResolvedRef.current = false;
    pendingPromiseRef.current = null;
  }, []);

  const ensureGeoLoaded = useCallback(async () => {
    if (hasResolvedRef.current) {
      return geoCountryCode;
    }

    if (pendingPromiseRef.current) {
      return pendingPromiseRef.current;
    }

    const promise = (async () => {
      setGeoLoading(true);
      setGeoReady(false);

      try {
        if (!navigator.geolocation) {
          setGeoCountryCode(null);
          hasResolvedRef.current = true;
          return null;
        }

        const position = await Promise.race([
          getCurrentPosition(),
          new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 4000)),
        ]);

        if (position?.latitude && position?.longitude) {
          const code = await getCountryCode(position.latitude, position.longitude);
          setGeoCountryCode(code || null);
          hasResolvedRef.current = true;
          return code || null;
        }

        setGeoCountryCode(null);
        hasResolvedRef.current = true;
        return null;
      } catch {
        setGeoCountryCode(null);
        hasResolvedRef.current = true;
        return null;
      } finally {
        setGeoLoading(false);
        setGeoReady(true);
        pendingPromiseRef.current = null;
      }
    })();

    pendingPromiseRef.current = promise;
    return promise;
  }, [geoCountryCode]);

  const value = useMemo(
    () => ({
      geoCountryCode,
      geoReady,
      geoLoading,
      ensureGeoLoaded,
      resetGeoState,
      isInPoland: geoCountryCode === "PL",
    }),
    [geoCountryCode, geoReady, geoLoading, ensureGeoLoaded, resetGeoState],
  );

  return <GeoContext.Provider value={value}>{children}</GeoContext.Provider>;
};

export const useGeo = () => {
  const context = useContext(GeoContext);

  if (!context) {
    throw new Error("useGeo must be used within GeoProvider");
  }

  return context;
};
